import React from "react";
import { Link } from "react-router-dom";
import { Container, Section, H1, H2, Breadcrumbs, Eyebrow } from "../components/Primitives";
import { MukhiSvg, MukhiSpecial } from "../components/MukhiSvg";
import { Reveal, Stagger, StaggerItem } from "../lib/motion";
import { useSEO, breadcrumbSchema } from "../lib/seo";

/**
 * /guides/rudraksha-mukhi — a full illustrated reference to the twenty-one
 * mukhi plus the five joined forms.
 *
 * Every diagram is generated from the mukhi count itself (see MukhiSvg): the
 * 5 mukhi diagram has exactly five clefts because it is drawn from the number
 * five. That is deliberate, and it is why we do not use AI-generated art or
 * photographs on this page — on a site whose entire proposition is countable
 * accuracy, a diagram that teaches the wrong count would be worse than none.
 *
 * The founder rule applies: these diagrams live on the guide page only. They
 * do not appear on any product listing.
 */

const MUKHI_ATTRIBUTIONS = {
  1:  { graha: "Sun · Surya",     note: "The most counterfeited count in the trade. Half-moon form in Java is the common natural variant." },
  2:  { graha: "Moon · Chandra",  note: "Two clefts, two chambers. Traditionally associated with partnership and family." },
  3:  { graha: "Mars · Mangal",   note: "Associated with Agni — energy and initiative in the classical scheme." },
  4:  { graha: "Mercury · Budh",  note: "Traditionally linked with Brahma and the pursuit of study." },
  5:  { graha: "Jupiter · Guru",  note: "The commonest count. The everyday working bead of japa practice." },
  6:  { graha: "Venus · Shukra",  note: "Associated with Kartikeya (Muruga) in the tradition." },
  7:  { graha: "Saturn · Shani",  note: "Linked with Mahalakshmi in some sources, Anantnag in others." },
  8:  { graha: "Ketu",            note: "Associated with Ganesha — removal of obstacles is the traditional attribution, worded as attribution." },
  9:  { graha: "Ketu",            note: "Linked with Durga's nine forms (Navdurga)." },
  10: { graha: "Sun (secondary)", note: "Some sources link the ten mukhi to Vishnu and the ten avataras; others treat it as graha-neutral. We say so." },
  11: { graha: "Ketu",            note: "Higher counts are where carving fraud concentrates. Read the X-ray first." },
  12: { graha: "Sun",             note: "Twelve Adityas — the twelve solar forms of the classical scheme." },
  13: { graha: "Venus · Shukra",  note: "Rare in supply, and correspondingly priced. Associated with Kamadeva in some texts." },
  14: { graha: "Saturn · Shani",  note: "The highest count in common trade. Traditionally linked with Hanuman." },
  15: { graha: "—",               note: "Linked with Pashupati (a form of Shiva) in later texts." },
  16: { graha: "—",               note: "Associated with the Mahakala form. Very rare." },
  17: { graha: "—",               note: "Linked with Saraswati in some Puranic sources." },
  18: { graha: "—",               note: "Associated with Bhoomi (Earth). Rarely encountered." },
  19: { graha: "—",               note: "Linked with Narayana in later devotional texts." },
  20: { graha: "—",               note: "Associated with Brahma. Extreme rarity — verify twice." },
  21: { graha: "—",               note: "Associated with Kubera in the tradition. If you are shown one at a normal-market price, that is the flag." },
};

const SPECIALS = [
  {
    kind: "gauri-shankar",
    name: "Gauri Shankar",
    subtitle: "Two naturally fused beads",
    body: "A single seed shell in which two beads have grown fused at the equator. Traditionally associated with the union of Shiva and Shakti. The fusion must be natural — glued pairs exist and are the counterfeit here; the X-ray shows the join.",
  },
  {
    kind: "ganesh",
    name: "Ganesh",
    subtitle: "Natural trunk-like protrusion",
    body: "A rudraksha whose surface carries a natural pronounced protrusion that resembles a trunk. Named for Ganesha in the tradition, worded as attribution rather than effect. The protrusion must be part of the shell — carved bumps are the fraud.",
  },
  {
    kind: "trijuti",
    name: "Trijuti",
    subtitle: "Three fused beads",
    body: "Three beads naturally fused into a single seed. Extremely rare in supply. Associated with the trimurti (Brahma-Vishnu-Shiva) in the tradition. A trijuti at a normal-market price is a fabricated one.",
  },
  {
    kind: "savar",
    name: "Savar",
    subtitle: "One-mukhi fused to a multi-mukhi bead",
    body: "A single-mukhi bead naturally fused to a multi-mukhi bead. The naming varies by region and by lineage; some sources call the same form Sawar. We use Savar and say so.",
  },
  {
    kind: "nandi",
    name: "Nandi (a.k.a. Gauri Path)",
    subtitle: "Vertically fused pair — regional naming",
    body: "Two beads fused pole-to-pole rather than equator-to-equator. The naming is genuinely disputed — some Nepal lineages use Nandi, some later North Indian sources use Gauri Path for the same form. This page uses Nandi. If your teacher uses Gauri Path, you are looking at the same thing.",
  },
];

function MukhiCard({ n, mukhi }) {
  return (
    <StaggerItem>
      <div className="border border-rule bg-surface p-6" data-testid={`mukhi-card-${n}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="ui text-[10px] font-medium uppercase tracking-[0.22em] text-dim">
              {n === 1 ? "1 mukhi" : `${n} mukhi`}
            </p>
            <p className="ui mt-1 text-[13px] text-gold">{mukhi.graha}</p>
          </div>
          <div className="text-ink shrink-0" aria-hidden="true">
            <MukhiSvg n={n} size={92} strokeWidth={1.2} />
          </div>
        </div>
        <p className="mt-4 text-[14.5px] leading-[1.65] text-ink" style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
          {mukhi.note}
        </p>
      </div>
    </StaggerItem>
  );
}

function SpecialCard({ s }) {
  return (
    <StaggerItem>
      <div className="border border-rule bg-surface p-7" data-testid={`mukhi-special-${s.kind}`}>
        <div className="flex items-start justify-between gap-5">
          <div>
            <p className="ui text-[10px] font-medium uppercase tracking-[0.22em] text-dim">Joined form</p>
            <h3 className="mt-1 font-serif text-[22px] leading-[1.15] text-ink" style={{ fontFamily: "Fraunces, Georgia, serif" }}>
              {s.name}
            </h3>
            <p className="ui mt-1 text-[13px] text-gold">{s.subtitle}</p>
          </div>
          <div className="text-ink shrink-0" aria-hidden="true">
            <MukhiSpecial kind={s.kind} size={112} />
          </div>
        </div>
        <p className="mt-4 text-[14.5px] leading-[1.65] text-ink" style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
          {s.body}
        </p>
      </div>
    </StaggerItem>
  );
}

export default function GuideRudrakshaMukhi() {
  const trail = [
    { label: "Home", to: "/" },
    { label: "Guides", to: "/guide" },
    { label: "Rudraksha · The mukhi reference" },
  ];

  useSEO({
    title: "Rudraksha — the mukhi reference (1–21 plus joined forms)",
    description:
      "Every mukhi from 1 to 21 illustrated from the number itself, plus the five joined forms — Gauri Shankar, Ganesh, Trijuti, Savar and Nandi. Attribution stated as attribution.",
    jsonLd: [breadcrumbSchema(trail)],
  });

  return (
    <>
      <Container className="pt-6"><Breadcrumbs trail={trail} /></Container>

      <Section className="py-14">
        <Container>
          <div className="max-w-[var(--measure-read)]">
            <Eyebrow>Guides · Rudraksha</Eyebrow>
            <H1 className="mt-3">The mukhi reference — 1 to 21, plus joined forms</H1>
            <p className="mt-5 text-[16px] leading-[1.7] text-ink" style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
              Every diagram on this page is drawn programmatically from the mukhi count itself.
              The 5 mukhi diagram has exactly five clefts because it was generated from the
              number five. The 21 mukhi has twenty-one. We do not use AI-generated illustrations
              or photographs here — on a site whose whole proposition is countable accuracy, a
              diagram that teaches the wrong count would be worse than none at all.
            </p>
            <p className="mt-4 text-[15px] italic leading-[1.65] text-dim" style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
              These are illustrations. They are not photographs of any specific bead. The
              photograph, the X-ray and the chamber count for each item in the catalogue live
              on that item's product page.
            </p>
          </div>
        </Container>
      </Section>

      <Section className="border-t border-rule/60 py-12">
        <Container>
          <Reveal>
            <p className="ui text-[10px] font-medium uppercase tracking-[0.22em] text-dim">The classical counts</p>
            <H2 className="mt-2">1 to 21 mukhi</H2>
          </Reveal>
          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="mukhi-grid">
            {Array.from({ length: 21 }, (_, i) => i + 1).map((n) => (
              <MukhiCard key={n} n={n} mukhi={MUKHI_ATTRIBUTIONS[n]} />
            ))}
          </Stagger>
        </Container>
      </Section>

      <Section className="border-t border-rule/60 py-12">
        <Container>
          <Reveal>
            <p className="ui text-[10px] font-medium uppercase tracking-[0.22em] text-dim">Natural fusions</p>
            <H2 className="mt-2">The five joined forms</H2>
            <p className="mt-4 max-w-[var(--measure-read)] text-[15px] leading-[1.65] text-ink" style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
              Rudraksha beads occasionally fuse during growth. Each form below is a natural
              fusion, not a paired assembly. Glued or wired pairs are the counterfeit here —
              the X-ray shows the shell continuity, and that is what settles it.
            </p>
          </Reveal>
          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2" data-testid="specials-grid">
            {SPECIALS.map((s) => <SpecialCard key={s.kind} s={s} />)}
          </Stagger>
        </Container>
      </Section>

      <Section className="border-t border-rule/60 py-12">
        <Container>
          <div className="max-w-[var(--measure-read)]">
            <Reveal>
              <p className="ui text-[10px] font-medium uppercase tracking-[0.22em] text-dim">Wording</p>
              <H2 className="mt-2">How the attributions here are worded</H2>
            </Reveal>
            <p className="mt-5 text-[15.5px] leading-[1.72] text-ink" style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
              Traditional attributions on this page are attributions, not effects. Classical
              sources associate a five-mukhi bead with Guru; classical sources link an eight
              mukhi with Ganesha. Where sources disagree — as they do for ten mukhi, and for
              the naming of the vertically-fused Nandi/Gauri Path form — the page says so
              rather than pick the most saleable reading. No claim is made that any of these
              beads will cure, treat or prevent anything.
            </p>
            <p className="mt-6 text-[14px] italic leading-[1.7] text-dim">
              If you were sold a rudraksha somewhere else and want a second opinion, our{" "}
              <Link to="/tools/verify-a-certificate" className="link-u">verify tool</Link>{" "}
              will check any seller's report number on the laboratory's own site — we store
              nothing, and you do not have to buy from us.
            </p>
          </div>
        </Container>
      </Section>
    </>
  );
}
